import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { debounceTime } from 'rxjs';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatDialog } from '@angular/material/dialog';
import moment from 'moment';

// Importaciones de entornos.
import { environment } from '../../../environments/environment';

// Importaciones de servicios.
import { ActivationService } from '../../services/activation.service';
import { DansapService } from '../../services/dansap.service';
import { HostService } from '../../services/host.service';
import { LicenseService } from '../../services/license.service';
import { LogService } from '../../services/log.service';
import { MeService } from '../../services/me.service';
import { ProductService } from '../../services/product.service';

// Importaciones de componentes.
import { ActivationDialogComponent } from '../../components/activation-dialog/activation-dialog.component';

// Importaciones de interfaces.
import { IActivation } from '../../interfaces/activation.interface';
import { ICustomer } from '../../interfaces/customer.interface';
import { IEmployee } from '../../interfaces/employee.interface';
import { IHost } from '../../interfaces/host.interface';
import { IHostResponse } from '../../interfaces/host-response.interface';
import { ILicense } from '../../interfaces/license.interface';
import { ILicenseResponse } from '../../interfaces/license-response.interface';
import { IProduct } from '../../interfaces/product.interface';

// Importaciones de modelos.
import { Activation } from '../../models/activation.model';
import { Customer } from '../../models/customer.model';
import { Employee } from '../../models/employee.model';
import { Host } from '../../models/host.model';
import { License } from '../../models/license.model';
import { Log } from '../../models/log.model';
import { Product } from '../../models/product.model';
import { Role } from '../../models/role.model';

// Importaciones de validadores.
import { expirationDateValidator } from '../../validators/expiration-date.validator';

/**
 * Componente de activación.
 *
 * Este componente gestiona el proceso de activación dentro de la aplicación, permitiendo visualizar,
 * seleccionar y activar elementos según sea necesario. También puede interactuar con otros servicios
 * para realizar activaciones externas.
 */
@Component({
  selector: 'app-activation',
  templateUrl: './activation.component.html',
  standalone: false,
})
export class ActivationComponent implements OnInit {
  /**
   * Indicador del estado de carga de los datos.
   *
   * Si es verdadero, se están cargando los datos.
   */
  loadingInitData: boolean = false;

  /**
   * Indicador del estado de carga de la búsqueda general del cliente.
   *
   * Si es verdadero, se está cargando el cliente.
   */
  loadingGeneralSearchCustomer: boolean = false;

  /**
   * Indicador del estado de carga de la búsqueda por referencia del cliente.
   *
   * Si es verdadero, se está cargando el cliente.
   */
  loadingReferenceSearchCustomer: boolean = false;

  /**
   * Indicador de carga para la generación de activación.
   *
   * Esta propiedad almacena un valor booleano que indica si
   * la operación de generación de activación está en progreso.
   * Se utiliza para mostrar un estado de carga en la interfaz
   * de usuario mientras se realiza la operación.
   */
  loadingGenerateActivation: boolean = false;

  /**
   * Errores generados durante la operación de activación.
   *
   * Esta propiedad almacena un arreglo de cadenas que representan
   * los mensajes de error que pueden surgir durante la operación
   * de generación de activación. Se utiliza para informar al
   * usuario sobre los problemas encontrados.
   */
  generateActivationErrors: string[] = [];

  /**
   * Contiene la información del producto actual.
   *
   * Se inicializa con una nueva instancia de `Product`, pero se actualizará
   * con los datos obtenidos del servicio correspondiente.
   */
  product: IProduct = new Product();

  /**
   * Lista de licencias disponibles.
   *
   * Esta propiedad almacena un arreglo de objetos de tipo `ILicense`,
   * que representan las licencias asociadas al producto actual.
   * Se utiliza para mostrar las licencias disponibles en la interfaz
   * de usuario y para seleccionar una licencia durante el proceso
   * de activación.
   */
  licenses: ILicense[] = [];

  /**
   * Lista de hosts disponibles.
   *
   * Esta propiedad almacena un arreglo de objetos de tipo `IHost`,
   * que representan los hosts disponibles para asociar a una activación.
   * Se utiliza para mostrar los hosts en la interfaz de usuario y
   * para seleccionar un host durante el proceso de activación.
   */
  hosts: IHost[] = [];

  /**
   * Lista de clientes buscados.
   *
   * Esta propiedad almacena un arreglo de objetos de tipo ICustomer,
   * que representan los clientes que han sido encontrados en
   * función de la búsqueda realizada. Se utiliza para mostrar
   * la lista de clientes en la interfaz de usuario.
   */
  searchedCustomers: ICustomer[] = [];

  /**
   * Valor de búsqueda ingresado.
   *
   * Esta propiedad almacena el valor actual que el usuario ha
   * ingresado en el campo de búsqueda. Se utiliza para filtrar
   * los resultados y determinar qué clientes se deben mostrar
   * en función de la búsqueda.
   */
  searchedValue: string = '';

  /**
   * Tipos de activación disponibles.
   *
   * Este arreglo contiene los diferentes tipos que un
   * usuario puede seleccionar al generar una activación.
   */
  types: { value: number; name: string }[] = [
    { value: 1, name: 'Tipo 1 (ej: 3GTW...)' },
    { value: 2, name: 'Tipo 2 (ej: 4GTW...)' },
    { value: 3, name: 'Tipo 3 (ej: 5GTW...)' },
  ];

  /**
   * Formulario reactivo para la activación.
   *
   * Este formulario está estructurado en varios grupos
   * que contienen controles específicos para la
   * activación, el cliente, la licencia y el host.
   */
  form: FormGroup = new FormGroup({
    license: new FormGroup({
      type: new FormControl(2, [
        Validators.required,
        Validators.min(1),
        Validators.max(3),
      ]),
      license: new FormControl('', Validators.required),
      host: new FormControl('', Validators.required),
    }),
    customer: new FormGroup({
      new: new FormControl(false, Validators.required),
      generalSearch: new FormControl(),
      referenceSearch: new FormControl(),
      reference: new FormControl(),
      nif: new FormControl('', Validators.required),
      name: new FormControl('', Validators.required),
      tradeName: new FormControl(),
      telephone: new FormControl('', Validators.required),
      province: new FormControl(),
      country: new FormControl(),
      address: new FormControl(),
      city: new FormControl(),
      postalCode: new FormControl(),
    }),
    activation: new FormGroup({
      code: new FormControl('', [
        Validators.required,
        Validators.pattern('^[A-Z]+$'),
        Validators.minLength(6),
        Validators.maxLength(6),
      ]),
      expirationDate: new FormControl(moment().add(4, 'days').startOf('day'), [
        Validators.required,
        expirationDateValidator(),
      ]),
      comment: new FormControl(),
    }),
  });

  /**
   * Getter para acceder al tipo de la licencia del formulario.
   *
   * @returns El control del tipo de licencia.
   */
  get formLicenseType(): AbstractControl | null {
    return this.form.get('license.type');
  }

  /**
   * Getter para acceder a la licencia en el formulario.
   *
   * @returns El control de la licencia.
   */
  get formLicenseLicense(): AbstractControl | null {
    return this.form.get('license.license');
  }

  /**
   * Getter para acceder al host en el formulario.
   *
   * @returns El control del host.
   */
  get formLicenseHost(): AbstractControl | null {
    return this.form.get('license.host');
  }

  /**
   * Getter para acceder al campo de nuevo cliente en el formulario.
   *
   * @returns El control del nuevo cliente.
   */
  get formCustomerNew(): AbstractControl | null {
    return this.form.get('customer.new');
  }

  /**
   * Getter para acceder a la búsqueda general del cliente en el formulario.
   *
   * @returns El control de la búsqueda general del cliente.
   */
  get formCustomerGeneralSearch(): AbstractControl | null {
    return this.form.get('customer.generalSearch');
  }

  /**
   * Getter para acceder a la búsqueda por referencia del cliente en el formulario.
   *
   * @returns El control de la búsqueda por referencia del cliente.
   */
  get formCustomerReferenceSearch(): AbstractControl | null {
    return this.form.get('customer.referenceSearch');
  }

  /**
   * Getter para acceder a la referencia del cliente en el formulario.
   *
   * @returns El control de la referencia del cliente.
   */
  get formCustomerReference(): AbstractControl | null {
    return this.form.get('customer.reference');
  }

  /**
   * Obtiene el campo NIF del cliente del formulario.
   *
   * @returns El control del formulario asociado
   * al NIF del cliente.
   */
  get formCustomerNif(): AbstractControl | null {
    return this.form.get('customer.nif');
  }

  /**
   * Obtiene el campo nombre del cliente del formulario.
   *
   * @returns El control del formulario asociado
   * al nombre del cliente.
   */
  get formCustomerName(): AbstractControl | null {
    return this.form.get('customer.name');
  }

  /**
   * Obtiene el campo nombre comercial del cliente del formulario.
   *
   * @returns El control del formulario asociado
   * al nombre comercial del cliente.
   */
  get formCustomerTradeName(): AbstractControl | null {
    return this.form.get('customer.tradeName');
  }

  /**
   * Obtiene el campo teléfono del cliente del formulario.
   *
   * @returns El control del formulario asociado
   * al teléfono del cliente.
   */
  get formCustomerTelephone(): AbstractControl | null {
    return this.form.get('customer.telephone');
  }

  /**
   * Obtiene el campo provincia del cliente del formulario.
   *
   * @returns El control del formulario asociado
   * a la provincia del cliente.
   */
  get formCustomerProvince(): AbstractControl | null {
    return this.form.get('customer.province');
  }

  /**
   * Obtiene el campo país del cliente del formulario.
   *
   * @returns El control del formulario asociado
   * al país del cliente.
   */
  get formCustomerCountry(): AbstractControl | null {
    return this.form.get('customer.country');
  }

  /**
   * Obtiene el campo dirección del cliente del formulario.
   *
   * @returns El control del formulario asociado
   * a la dirección del cliente.
   */
  get formCustomerAddress(): AbstractControl | null {
    return this.form.get('customer.address');
  }

  /**
   * Obtiene el campo ciudad del cliente del formulario.
   *
   * @returns El control del formulario asociado
   * a la ciudad del cliente.
   */
  get formCustomerCity(): AbstractControl | null {
    return this.form.get('customer.city');
  }

  /**
   * Obtiene el campo código postal del cliente del formulario.
   *
   * @returns El control del formulario asociado
   * al código postal del cliente.
   */
  get formCustomerPostalCode(): AbstractControl | null {
    return this.form.get('customer.postalCode');
  }

  /**
   * Obtiene el campo código de activación del formulario.
   *
   * @returns El control del formulario asociado
   * al código de activación.
   */
  get formActivationCode(): AbstractControl | null {
    return this.form.get('activation.code');
  }

  /**
   * Obtiene la fecha de expiración de la activación del formulario.
   *
   * @returns El control del formulario asociado
   * a la fecha de expiración de la activación.
   */
  get formActivationExpirationDate(): AbstractControl | null {
    return this.form.get('activation.expirationDate');
  }

  /**
   * Obtiene el campo comentario de la activación del formulario.
   *
   * @returns El control del formulario asociado
   * al comentario de la activación.
   */
  get formActivationComment(): AbstractControl | null {
    return this.form.get('activation.comment');
  }

  /**
   * Constructor de la clase.
   *
   * Este constructor inicializa los servicios necesarios para
   * el funcionamiento del componente. Cada servicio se inyecta
   * mediante la inyección de dependencias de Angular, lo que
   * permite acceder a sus métodos y propiedades a lo largo
   * del ciclo de vida del componente. Los servicios incluyen
   * funcionalidades relacionadas con la activación, autenticación,
   * gestión de hosts, licencias y la interacción con el usuario
   * a través de diálogos. Además, se inyecta el enrutador para
   * la navegación entre componentes.
   *
   * @param activationService - Servicio para gestionar activaciones.
   * @param dansapService - Servicio para interactuar con la API de Dansap.
   * @param hostService - Servicio para gestionar hosts.
   * @param licenseService - Servicio para gestionar licencias.
   * @param logService - Servicio para gestionar los registros.
   * @param matDialog - Servicio para gestionar diálogos en la interfaz.
   * @param meService - Servicio para gestionar la información del usuario autenticado.
   * @param productService - Servicio para gestionar datos del producto.
   * @param route - Servicio para gestionar la ruta activa.
   */
  constructor(
    private activationService: ActivationService,
    private dansapService: DansapService,
    private hostService: HostService,
    private licenseService: LicenseService,
    private logService: LogService,
    private matDialog: MatDialog,
    private meService: MeService,
    private productService: ProductService,
    private route: ActivatedRoute
  ) {}

  /**
   * Método de inicialización del componente.
   *
   * Este método se ejecuta al inicializar el componente y configura
   * los datos y las suscripciones necesarias para su funcionamiento.
   */
  async ngOnInit(): Promise<void> {
    // Obtener e inicializar los datos.
    await this.getInitData();

    // Suscripciones a los cambios de los campos del formulario.
    this.subscribeToFormChanges(
      this.formCustomerGeneralSearch,
      (value: string) => this.onInputCustomerSearch(value)
    );

    this.subscribeToFormChanges(
      this.formCustomerReferenceSearch,
      (value: string) => this.onInputCustomerSearch(value, true)
    );
  }

  /**
   * Maneja el cambio en el estado del toggle "¿Es un cliente nuevo?".
   *
   * Esta función se ejecuta cuando el usuario cambia el valor del toggle
   * que indica si el cliente es nuevo o existente. Si el cliente es nuevo,
   * se reinician todos los campos del formulario relacionados con el cliente
   * para asegurarse de que estén vacíos y listos para ingresar nueva información.
   */
  onCustomerNewChange(): void {
    this.resetCustomerForm();
  }

  /**
   * Maneja la selección de un cliente en la búsqueda automática.
   *
   * Esta función se ejecuta cuando un usuario selecciona un cliente
   * de la lista de autocompletado. Se espera que el evento
   * contenga información sobre el cliente seleccionado, lo que
   * Permitirá obtener el objeto del cliente seleccionado
   * y cargar-lo al formulario.
   *
   * @param event - El evento que contiene la información sobre el cliente seleccionado.
   */
  onCustomerSearchSelected(event: MatAutocompleteSelectedEvent): void {
    this.formCustomerGeneralSearch?.setValue(this.searchedValue);

    const selectedCustomer = this.searchedCustomers.find(
      (customer: ICustomer) => customer.reference === event.option.value
    );

    if (selectedCustomer) {
      this.updateCustomerForm(selectedCustomer);
    }
  }

  /**
   * Genera una activación a través de los datos del empleado actual
   * y los datos del formulario, creando un objeto de activación,
   * de manera asíncrona.
   */
  async generateActivation(): Promise<void> {
    try {
      this.loadingGenerateActivation = true;
      this.generateActivationErrors = [];

      // Comprobar si el formulario es válido.
      if (this.form.invalid) {
        this.generateActivationErrors.push(
          'El formulario contiene errores. Revisa los campos e inténtalo nuevamente.'
        );
        this.form.markAllAsTouched();
        this.loadingGenerateActivation = false;
        return;
      }

      // Obtener el empleado actual.
      const meResponse: IEmployee = await this.meService.get();
      const employee: IEmployee = new Employee({
        ...meResponse,
        role: new Role(meResponse.role),
      });

      // Obtener y validar la licencia seleccionada.
      const license = this.getSelectedLicense();
      if (!license) {
        this.generateActivationErrors.push('No se ha encontrado la licencia.');
        this.loadingGenerateActivation = false;
        return;
      }

      // Obtener y validar el host seleccionado.
      const host = this.getSelectedHost();
      if (!host) {
        this.generateActivationErrors.push('No se ha encontrado el host.');
        this.loadingGenerateActivation = false;
        return;
      }

      // Inicializar el objeto cliente.
      const customer = this.getCustomerFromForm();

      // Calcular días y tipo de expiración.
      const expirationDays = this.calculateExpirationDays();
      const expirationType = this.getExpirationType(expirationDays);

      // Crear el objeto de la activación.
      const activation = new Activation({
        employee,
        customer,
        license,
        host,
        code: this.formActivationCode?.value,
        expirationType,
        expirationDays,
        comment: this.formActivationComment?.value,
        type: Number(this.formLicenseType?.value),
      });

      // Generar la activación.
      const activationResponse = await this.activationService.create(
        activation
      );
      activation.id = activationResponse.id;
      activation.serial = activationResponse.serial;
      activation.password = activationResponse.password;

      // Registrar la activación.
      await this.logActivationCreation(activation);

      // Enviar la activación al Dansap.
      if (await this.sendActivationToDansap(activation)) {
        activation.sent = 1;
        await this.updateActivation(activation);
      }

      // Mostrar el diálogo con los datos de la activación.
      this.matDialog.open(ActivationDialogComponent, {
        width: '500px',
        maxWidth: '100%',
        disableClose: true,
        data: activation,
      });
    } catch (errors: any) {
      this.generateActivationErrors.push(...errors);
    } finally {
      this.loadingGenerateActivation = false;
    }
  }

  /**
   * Obtiene e inicializa los datos necesarios para el componente.
   *
   * Esta función realiza las llamadas a los servicios o procesos asíncronos
   * necesarios para cargar la información inicial del componente. Al finalizar,
   * todos los datos requeridos estarán disponibles para su uso en el componente.
   */
  private async getInitData(): Promise<void> {
    try {
      this.loadingInitData = true;

      // Inicializar valores predeterminados.
      this.initializeDefaults();

      // Obtener el ID del producto de la ruta.
      const productId = this.getProductIdFromRoute();
      if (!productId) {
        this.loadingInitData = false;
        return;
      }

      // Obtener el producto.
      await this.loadProduct(productId);

      // Obtener licencias y hosts.
      await this.loadLicenses();
      await this.loadHosts();
    } finally {
      this.loadingInitData = false;
    }
  }

  /**
   * Inicializa los valores predeterminados del componente.
   *
   * Esta función establece valores iniciales para las propiedades del componente,
   * como el empleado, el producto, las licencias y los hosts. También reinicia
   * los valores de los campos del formulario relacionados con las licencias y los hosts.
   */
  private initializeDefaults(): void {
    this.product = new Product();
    this.licenses = [];
    this.hosts = [];
    this.formLicenseLicense?.setValue(null);
    this.formLicenseHost?.setValue(null);
  }

  /**
   * Obtiene el ID del producto desde la ruta activa.
   *
   * Esta función extrae el parámetro `id` de la ruta activa y lo convierte en un número.
   * Si el parámetro no es válido o no está presente, devuelve `null`.
   *
   * @returns El ID del producto como un número o `null` si no es válido.
   */
  private getProductIdFromRoute(): number | null {
    const param = this.route.snapshot.paramMap.get('id');
    return param && !isNaN(+param) ? +param : null;
  }

  /**
   * Carga los datos del producto especificado.
   *
   * Esta función realiza una solicitud al servicio correspondiente para obtener
   * los datos del producto identificado por el `productId` proporcionado y los
   * asigna a la propiedad `product` del componente.
   *
   * @param productId - El ID del producto que se desea cargar.
   */
  private async loadProduct(productId: number): Promise<void> {
    const productResponse: IProduct = await this.productService.get(productId);
    this.product = new Product(productResponse);
  }

  /**
   * Carga las licencias asociadas al producto actual.
   *
   * Esta función realiza una solicitud al servicio correspondiente para obtener
   * las licencias asociadas al producto actual. Las licencias se transforman en
   * objetos de tipo `License` y se asignan a la propiedad `licenses`. Además,
   * se establece el valor inicial del campo de licencia en el formulario.
   */
  private async loadLicenses(): Promise<void> {
    if (this.product.id) {
      const licenseResponse: ILicenseResponse =
        await this.licenseService.getAll(this.product.id, 0);
      this.licenses = licenseResponse.licenses.map(
        (license: ILicense) =>
          new License({ ...license, product: new Product(license.product) })
      );
      this.formLicenseLicense?.setValue(
        this.licenses.length ? this.licenses[0].id : null
      );
    }
  }

  /**
   * Carga los hosts disponibles.
   *
   * Esta función realiza una solicitud al servicio correspondiente para obtener
   * la lista de hosts disponibles. Los hosts se transforman en objetos de tipo `Host`
   * y se asignan a la propiedad `hosts`. Además, se establece el valor inicial del
   * campo de host en el formulario.
   */
  private async loadHosts(): Promise<void> {
    const responseHosts: IHostResponse = await this.hostService.getAll(0);
    this.hosts = responseHosts.hosts.map((host: IHost) => new Host(host));
    this.formLicenseHost?.setValue(this.hosts.length ? this.hosts[0].id : null);
  }

  /**
   * Maneja la entrada en el campo de búsqueda de clientes.
   *
   * Esta función procesa el valor introducido por el usuario y realiza una búsqueda
   * en la API de Dansap. Dependiendo del tipo de búsqueda (general o por referencia),
   * actualiza los datos del formulario o muestra los errores correspondientes.
   *
   * @param value - El valor introducido en el campo de búsqueda.
   * @param isReferenceSearch - Indica si la búsqueda se realiza por referencia (`true`) o de forma general (`false`).
   */
  private async onInputCustomerSearch(
    value: string,
    isReferenceSearch: boolean = false
  ): Promise<void> {
    const searchValue = value.trim();

    if (searchValue === this.searchedValue) {
      return;
    }

    this.searchedValue = searchValue;
    this.searchedCustomers = [];
    this.resetCustomerForm();

    if (!searchValue) {
      return;
    }

    if (isReferenceSearch) {
      // Búsqueda por referencia.
      const reference = parseInt(searchValue);
      if (isNaN(reference)) {
        this.setFormError(this.formCustomerReferenceSearch, 'invalidReference');
        return;
      }

      try {
        this.loadingReferenceSearchCustomer = true;
        const dansapCustomer: any = await this.dansapService.getClientByCode(
          reference
        );

        // Añadir un registro de búsqueda de cliente por referencia al Dansap.
        try {
          await this.logService.create(
            new Log({
              description: `Ha buscado el cliente con la referencia '${reference}' al Dansap.`,
            })
          );
        } catch (errors: any) {
          if (!environment.production) {
            console.warn(
              'No se ha podido guardar el registro de búsqueda de cliente por referencia al Dansap.'
            );
          }
        }

        if (dansapCustomer) {
          const customer: ICustomer =
            this.getCustomerFromDansap(dansapCustomer);
          this.updateCustomerForm(customer);
        }
      } catch (errors: any) {
        this.setFormError(
          this.formCustomerReferenceSearch,
          'customerSearchError'
        );
      } finally {
        this.loadingReferenceSearchCustomer = false;
      }
    } else {
      // Búsqueda general.
      try {
        this.loadingGeneralSearchCustomer = true;
        const dansapCustomers: any[] =
          await this.dansapService.getClientsBySearchTerm(searchValue);

        // Añadir un registro de búsqueda de cliente.
        try {
          await this.logService.create(
            new Log({
              description: `Ha buscado el cliente '${searchValue}' al Dansap.`,
            })
          );
        } catch (errors: any) {
          if (!environment.production) {
            console.warn(
              'No se ha podido guardar el registro de búsqueda de cliente al Dansap.'
            );
          }
        }

        if (dansapCustomers && Array.isArray(dansapCustomers)) {
          this.searchedCustomers = dansapCustomers.map((dansapCustomer: any) =>
            this.getCustomerFromDansap(dansapCustomer)
          );
        }
      } catch (errors: any) {
        this.setFormError(
          this.formCustomerGeneralSearch,
          'customerSearchError'
        );
      } finally {
        this.loadingGeneralSearchCustomer = false;
      }
    }
  }

  /**
   * Restablece los campos del formulario relacionados con el cliente.
   *
   * Esta función limpia todos los valores de los campos del formulario
   * asociados con los datos del cliente.
   */
  private resetCustomerForm(): void {
    this.formCustomerReference?.reset();
    this.formCustomerNif?.reset();
    this.formCustomerName?.reset();
    this.formCustomerTradeName?.reset();
    this.formCustomerTelephone?.reset();
    this.formCustomerProvince?.reset();
    this.formCustomerCountry?.reset();
    this.formCustomerAddress?.reset();
    this.formCustomerCity?.reset();
    this.formCustomerPostalCode?.reset();
  }

  /**
   * Actualiza los campos del formulario con los datos de un cliente.
   *
   * Esta función asigna los valores del cliente proporcionado a los campos correspondientes
   * del formulario y marca los campos como tocados para que se reflejen los cambios en la interfaz.
   *
   * @param customer - El objeto que contiene los datos del cliente.
   */
  private updateCustomerForm(customer: ICustomer): void {
    this.formCustomerReference?.setValue(customer.reference);
    this.formCustomerReference?.markAsTouched();
    this.formCustomerNif?.setValue(customer.nif);
    this.formCustomerNif?.markAsTouched();
    this.formCustomerName?.setValue(customer.name);
    this.formCustomerName?.markAsTouched();
    this.formCustomerTradeName?.setValue(customer.tradeName);
    this.formCustomerTradeName?.markAsTouched();
    this.formCustomerTelephone?.setValue(customer.telephone);
    this.formCustomerTelephone?.markAsTouched();
    this.formCustomerProvince?.setValue(customer.province);
    this.formCustomerProvince?.markAsTouched();
    this.formCustomerAddress?.setValue(customer.address);
    this.formCustomerAddress?.markAsTouched();
    this.formCustomerCity?.setValue(customer.city);
    this.formCustomerCity?.markAsTouched();
    this.formCustomerPostalCode?.setValue(customer.postalCode);
    this.formCustomerPostalCode?.markAsTouched();
    this.formCustomerCountry?.setValue(customer.country);
    this.formCustomerCountry?.markAsTouched();
  }

  /**
   * Obtiene la licencia seleccionada del formulario.
   *
   * Esta función busca en la lista de licencias disponibles la que coincide
   * con el ID seleccionado en el formulario.
   *
   * @returns La licencia seleccionada o `undefined` si no se encuentra.
   */
  private getSelectedLicense(): ILicense | undefined {
    return this.licenses.find(
      (license) => license.id == parseInt(this.formLicenseLicense?.value)
    );
  }

  /**
   * Obtiene el host seleccionado del formulario.
   *
   * Esta función busca en la lista de hosts disponibles el que coincide
   * con el ID seleccionado en el formulario.
   *
   * @returns El host seleccionado o `undefined` si no se encuentra.
   */
  private getSelectedHost(): IHost | undefined {
    return this.hosts.find(
      (host) => host.id == parseInt(this.formLicenseHost?.value)
    );
  }

  /**
   * Crea un objeto de cliente a partir de los datos del formulario.
   *
   * Esta función recopila los valores de los campos del formulario relacionados
   * con el cliente y los utiliza para inicializar un nuevo objeto de tipo `Customer`.
   *
   * @returns Un objeto `Customer` con los datos del formulario.
   */
  private getCustomerFromForm(): ICustomer {
    return new Customer({
      reference: this.formCustomerReference?.value,
      nif: this.formCustomerNif?.value,
      name: this.formCustomerName?.value,
      tradeName: this.formCustomerTradeName?.value,
      address: this.formCustomerAddress?.value,
      telephone: this.formCustomerTelephone?.value,
      city: this.formCustomerCity?.value,
      province: this.formCustomerProvince?.value,
      postalCode: this.formCustomerPostalCode?.value,
      country: this.formCustomerCountry?.value,
    });
  }

  /**
   * Crea un objeto de cliente a partir de los datos obtenidos del Dansap.
   *
   * Esta función transforma los datos de un cliente proporcionados por la API de Dansap
   * en un objeto de tipo `Customer`.
   *
   * @param dansapCustomer - Los datos del cliente obtenidos del Dansap.
   *
   * @returns Un objeto `Customer` con los datos transformados.
   */
  private getCustomerFromDansap(dansapCustomer: any): ICustomer {
    return new Customer({
      reference: dansapCustomer.codigo_cliente,
      nif: dansapCustomer.nif,
      name: dansapCustomer.nombre,
      tradeName: dansapCustomer.nombre_comercial,
      address: dansapCustomer.direccion,
      telephone: dansapCustomer.telefono,
      city: dansapCustomer.poblacion,
      province: dansapCustomer.provincia,
      postalCode: dansapCustomer.codigo_postal,
      country: dansapCustomer.pais,
    });
  }

  /**
   * Calcula los días de expiración a partir de la fecha seleccionada en el formulario.
   *
   * Esta función calcula la diferencia en días entre la fecha de expiración seleccionada
   * y la fecha actual.
   *
   * @returns El número de días de expiración.
   */
  private calculateExpirationDays(): number {
    return this.formActivationExpirationDate?.value.diff(
      moment().startOf('day'),
      'days'
    );
  }

  /**
   * Determina el tipo de expiración basado en el número de días proporcionado.
   *
   * Esta función evalúa el número de días de expiración y devuelve una cadena
   * descriptiva que indica el rango de tiempo correspondiente.
   *
   * @param expirationDays - Número de días de expiración.
   *
   * @returns Una cadena que describe el rango de tiempo de expiración.
   */
  private getExpirationType(expirationDays: number): string {
    if (expirationDays > 3650) return 'Más de diez años';
    if (expirationDays > 365) return 'Más de un año';
    if (expirationDays > 90) return 'Más de tres meses';
    if (expirationDays > 30) return 'Más de un mes';
    return 'Menos de un mes';
  }

  /**
   * Registra la creación de una activación en el sistema de logs.
   *
   * Esta función envía un registro al servicio de logs indicando que se ha creado
   * una nueva activación.
   *
   * @param activation - El objeto de activación que se ha creado.
   *
   * @returns `true` si el registro se creó correctamente, `false` en caso contrario.
   */
  private async logActivationCreation(
    activation: IActivation
  ): Promise<boolean> {
    try {
      await this.logService.create(
        new Log({
          description: `Ha creado la activación '${activation.id}'.`,
        })
      );
      return true;
    } catch (errors: any) {
      if (!environment.production) {
        console.warn(
          'No se ha podido guardar el registro de creación de una activación.'
        );
      }
      return false;
    }
  }

  /**
   * Envía una activación al Dansap.
   *
   * Esta función realiza una solicitud al servicio de Dansap para enviar
   * los datos de una activación. Si ocurre un error, se registra en los errores
   * de activación.
   *
   * @param activation - El objeto de activación que se enviará.
   *
   * @returns `true` si la activación se envió correctamente, `false` en caso contrario.
   */
  private async sendActivationToDansap(
    activation: IActivation
  ): Promise<boolean> {
    try {
      await this.dansapService.sendActivation(activation);
      return true;
    } catch (errors: any) {
      this.generateActivationErrors.push(
        'No se ha podido enviar la activación al Dansap.'
      );
      return false;
    }
  }

  /**
   * Actualiza una activación en el sistema.
   *
   * Esta función realiza una solicitud al servicio de activaciones para actualizar
   * los datos de una activación existente. Si ocurre un error, se registra en los errores
   * de activación.
   *
   * @param activation - El objeto de activación que se actualizará.
   *
   * @returns `true` si la activación se actualizó correctamente, `false` en caso contrario.
   */
  private async updateActivation(activation: IActivation): Promise<boolean> {
    try {
      await this.activationService.update(activation);
      return true;
    } catch (errors: any) {
      this.generateActivationErrors.push(
        'No se ha podido actualizar la activación.'
      );
      return false;
    }
  }

  /**
   * Se suscribe a los cambios en un control del formulario.
   *
   * Esta función se utiliza para escuchar los cambios en el valor de un control específico
   * del formulario y ejecutar una función de devolución de llamada cuando se detecta un cambio.
   *
   * @param control - El control del formulario al que se desea suscribir.
   * @param callback - La función de devolución de llamada que se ejecutará cuando cambie el valor.
   */
  private subscribeToFormChanges(
    control: AbstractControl | null,
    callback: (value: string) => void
  ): void {
    control?.valueChanges.pipe(debounceTime(1000)).subscribe(callback);
  }

  /**
   * Establece un error en un control del formulario.
   *
   * Esta función asigna un error específico a un control del formulario
   * y marca el control como tocado para que el error sea visible en la interfaz.
   *
   * @param control - El control del formulario donde se establecerá el error.
   * @param key - La clave del error que se establecerá.
   * @param value - El valor del error que se establecerá (por defecto es `true`).
   */
  private setFormError(
    control: AbstractControl | null,
    key: string,
    value: any = true
  ): void {
    control?.setErrors({ [key]: value });
    control?.markAsTouched();
  }
}
